import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { salesApi } from '../api/salesApi';
import { useToast } from '../../../shared/components/toast/useToast';
import type {
  CreateSalesFeaturePayload,
  CreateSalesInvoicePayload,
  PersistSelectionPayload,
  SalesCalculatorPreviewPayload,
  SalesFeatureQuery,
  SendSalesInvoicePayload,
  SettableInvoiceStatus,
  UpdateSalesFeaturePayload,
  UpdateSalesInvoicePayload,
  UpdateSalesPricingAssumptionsPayload,
  UpdateSalesTermsTemplatePayload,
} from '../types';

const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

const KEY = 'sales';
const FEATURES = [KEY, 'features'];
const INVOICES = [KEY, 'invoices'];
const ASSUMPTIONS = [KEY, 'assumptions'];
const TERMS = [KEY, 'terms-template'];

// ---- Feature catalogue ----

export const useSalesFeatures = (query: SalesFeatureQuery = {}) =>
  useQuery({
    queryKey: [...FEATURES, query],
    queryFn: () => salesApi.listFeatures(query),
    staleTime: 60 * 1000,
  });

export const useCreateSalesFeature = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateSalesFeaturePayload) => salesApi.createFeature(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FEATURES });
      toast.success('Feature added to the price book');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to add feature')),
  });
};

export const useUpdateSalesFeature = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSalesFeaturePayload }) =>
      salesApi.updateFeature(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FEATURES });
      toast.success('Feature updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update feature')),
  });
};

export const useDeactivateSalesFeature = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => salesApi.deactivateFeature(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FEATURES });
      toast.success('Feature deactivated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to deactivate feature')),
  });
};

// ---- Calculator ----

/**
 * Preview is a POST (it computes server-side) but is read-only, so it is modelled
 * as a mutation the panel fires on demand rather than a query — that keeps the
 * "nothing persisted" semantics obvious at the call site.
 */
export const useCalculatorPreview = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: SalesCalculatorPreviewPayload) => salesApi.preview(data),
    onError: (err: unknown) => toast.error(errMsg(err, 'Could not compute pricing')),
  });
};

export const usePersistSelection = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: PersistSelectionPayload) => salesApi.persistSelection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'selections'] });
      toast.success('Quote saved — you can now raise an invoice from it');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to save the quote')),
  });
};

export const useSalesSelection = (id: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, 'selections', id],
    queryFn: () => salesApi.getSelection(id as string),
    enabled: !!id,
    staleTime: 60 * 1000,
  });

// ---- Invoices ----

export const useSalesInvoices = () =>
  useQuery({
    queryKey: INVOICES,
    queryFn: () => salesApi.listInvoices(),
    staleTime: 30 * 1000,
  });

export const useSalesInvoice = (id: string | null | undefined) =>
  useQuery({
    queryKey: [...INVOICES, id],
    queryFn: () => salesApi.getInvoice(id as string),
    enabled: !!id,
    staleTime: 30 * 1000,
  });

export const useCreateSalesInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateSalesInvoicePayload) => salesApi.createInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVOICES });
      toast.success('Draft invoice created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create invoice')),
  });
};

export const useUpdateSalesInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSalesInvoicePayload }) =>
      salesApi.updateInvoice(id, data),
    onSuccess: (_res, { id }) => {
      queryClient.invalidateQueries({ queryKey: INVOICES });
      queryClient.invalidateQueries({ queryKey: [...INVOICES, id] });
      toast.success('Invoice updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update invoice')),
  });
};

export const useSendSalesInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SendSalesInvoicePayload }) =>
      salesApi.sendToCustomer(id, data),
    onSuccess: (_res, { id }) => {
      queryClient.invalidateQueries({ queryKey: INVOICES });
      queryClient.invalidateQueries({ queryKey: [...INVOICES, id] });
      toast.success('Invoice sent to the prospect');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to send invoice')),
  });
};

export const useSetSalesInvoiceStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: SettableInvoiceStatus }) =>
      salesApi.setInvoiceStatus(id, status),
    onSuccess: (_res, { id, status }) => {
      queryClient.invalidateQueries({ queryKey: INVOICES });
      queryClient.invalidateQueries({ queryKey: [...INVOICES, id] });
      toast.success(status === 'VOID' ? 'Invoice voided' : 'Invoice cancelled');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update invoice status')),
  });
};

export const useRevokeSalesInvoiceToken = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => salesApi.revokeToken(id),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: INVOICES });
      queryClient.invalidateQueries({ queryKey: [...INVOICES, id] });
      toast.success('Public link revoked');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to revoke the link')),
  });
};

export const useDownloadSalesInvoicePdf = () => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, invoiceNumber }: { id: string; invoiceNumber: string }) =>
      salesApi.downloadInvoicePdf(id, invoiceNumber),
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to download the PDF')),
  });
};

// ---- Config ----

export const useSalesAssumptions = () =>
  useQuery({
    queryKey: ASSUMPTIONS,
    queryFn: () => salesApi.getAssumptions(),
    staleTime: 5 * 60 * 1000,
  });

export const useUpdateSalesAssumptions = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: UpdateSalesPricingAssumptionsPayload) => salesApi.updateAssumptions(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ASSUMPTIONS });
      // Assumptions feed every computation, so any cached quote is now stale.
      queryClient.invalidateQueries({ queryKey: [KEY, 'selections'] });
      toast.success('Pricing assumptions saved');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to save assumptions')),
  });
};

export const useSalesTermsTemplate = () =>
  useQuery({
    queryKey: TERMS,
    queryFn: () => salesApi.getTermsTemplate(),
    staleTime: 5 * 60 * 1000,
  });

export const useUpdateSalesTermsTemplate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: UpdateSalesTermsTemplatePayload) => salesApi.updateTermsTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TERMS });
      toast.success('Terms template saved');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to save the terms template')),
  });
};
